import { Link } from '@remix-run/react'
import { useTranslation } from 'react-i18next'

import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import type { MediaUsage, MediaUsageItem } from '~/models/media.server'
import { Alert, Button, Dialog, Spinner, Tag } from '~/ui'

interface MediaDeleteDialogProps {
  isOpen: boolean
  isLoadingUsage: boolean
  mediaUsage: MediaUsage | null
  errorMessage?: string | null
  onCancel: () => void
  onConfirm: () => void
  onNavigate: () => void
}

export function MediaDeleteDialog({
  isOpen,
  isLoadingUsage,
  mediaUsage,
  errorMessage,
  onCancel,
  onConfirm,
  onNavigate,
}: MediaDeleteDialogProps) {
  const { t } = useTranslation()
  const shop = useShop()

  const hasUsage = mediaUsage && mediaUsage.totalCount > 0

  const getItemLink = (item: MediaUsageItem): string => {
    switch (item.targetType) {
      case 'product':
        return makeShopOwnPath(
          shop,
          `/products/${item.targetId}/${item.targetSlug}`
        )
      case 'collection':
        return makeShopOwnPath(
          shop,
          `/collections/${item.targetId}/${item.targetSlug}`
        )
      case 'shop_slider':
        return makeShopOwnPath(shop, '/')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <Dialog.Content>
        <Dialog.Title>
          {t('components.media_manager.delete_media_title')}
        </Dialog.Title>

        {isLoadingUsage ? (
          <div className="flex items-center justify-center py-4">
            <Spinner size="md" />
          </div>
        ) : hasUsage ? (
          <div className="flex flex-col gap-3 p-3">
            <Alert
              type="warning"
              className="p-2"
              title={t('components.media_manager.delete_media_in_use_warning')}
              description={t(
                'components.media_manager.delete_media_in_use_description'
              )}
            />

            <div>
              <p className="text-foreground mb-2 text-xs font-medium">
                {t('components.media_manager.delete_media_used_in')}
              </p>
              <div className="flex flex-wrap gap-2">
                {mediaUsage.items.map((item, index) => {
                  const link = getItemLink(item)
                  const key = item.targetId || `item-${index}`

                  return (
                    <Tag interactive key={key} asChild>
                      <Link to={link} onClick={onNavigate}>
                        {item.targetName}
                      </Link>
                    </Tag>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          <Dialog.Description className="text-foreground/64">
            {t('components.media_manager.delete_media_description')}
          </Dialog.Description>
        )}

        {errorMessage && (
          <div className="px-3">
            <Alert type="error" title={errorMessage} />
          </div>
        )}

        <Dialog.Actions>
          <Button variant="primary" onClick={onConfirm}>
            {hasUsage
              ? t('components.media_manager.delete_anyway')
              : t('action.confirm')}
          </Button>
          <Dialog.Close asChild>
            <Button variant="ghost" onClick={onCancel}>
              {t('action.cancel')}
            </Button>
          </Dialog.Close>
        </Dialog.Actions>
      </Dialog.Content>
    </Dialog>
  )
}
